import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar as CalendarIcon, DollarSign, Loader2, CheckCircle2, FileText } from 'lucide-react';
import axios from 'axios';
import { Calendar } from './ui/calendar';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriceCalculator = ({ showBooking, setShowBooking }) => {
  const [serviceType, setServiceType] = useState('');
  const [cleaningLevel, setCleaningLevel] = useState('standard');
  const [rooms, setRooms] = useState({});
  const [windowCount, setWindowCount] = useState(0);
  const [extraServices, setExtraServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');

  const serviceTypes = ['Residential', 'Commercial', 'Move-in/out', 'Weekly', 'Bi-Weekly', 'Monthly', 'Perinatal Support', 'Airbnb'];
  const roomTypes = ['Bedroom', 'Bathroom', 'Kitchen'];
  const extraServicesList = ['Oven Cleaning', 'Organizing', 'Refrigerator Cleaning', 'Laundry', 'Finished Basement'];
  const quoteOnlyServices = ['Commercial', 'Move-in/out', 'Perinatal Support', 'Airbnb'];
  const timeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

  const isQuoteOnly = quoteOnlyServices.includes(serviceType);

  const calculatePrice = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        service_type: serviceType,
        cleaning_level: cleaningLevel,
        rooms,
        window_count: windowCount,
        extra_services: extraServices
      });
      setCalculatedPrice(response.data);
    } catch (_error) {
      // Silent failure — user sees the last valid price until they change inputs
    }
  }, [serviceType, cleaningLevel, rooms, windowCount, extraServices]);

  useEffect(() => {
    if (serviceType) {
      calculatePrice();
    }
  }, [serviceType, calculatePrice]);

  const setRoomCount = (roomName, count) => {
    const newRooms = { ...rooms };
    if (count > 0) {
      newRooms[roomName] = count;
    } else {
      delete newRooms[roomName];
    }
    setRooms(newRooms);
  };

  const handleExtraServiceToggle = (service) => {
    if (extraServices.includes(service)) {
      setExtraServices(extraServices.filter(s => s !== service));
    } else {
      setExtraServices([...extraServices, service]);
    }
  };

  const handleSubmitBooking = async () => {
    if (!customerName || !customerEmail || !customerPhone || !address || !selectedDate || !selectedTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!calculatedPrice) {
      toast.error('Please wait for price calculation to complete');
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        service_type: serviceType,
        cleaning_level: cleaningLevel,
        scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
        scheduled_time: selectedTime,
        rooms,
        window_count: windowCount,
        bedrooms: rooms['Bedroom'] || 0,
        bathrooms: rooms['Bathroom'] || 0,
        extra_services: extraServices,
        address,
        notes,
        total_amount: calculatedPrice.total_price
      };

      await axios.post(`${API}/bookings`, bookingData);
      setBookingConfirmed(true);
      setLoading(false);
    } catch (_error) {
      toast.error('Failed to submit booking. Please try again or call 404-750-4446.');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setServiceType('');
    setCleaningLevel('standard');
    setRooms({});
    setWindowCount(0);
    setExtraServices([]);
    setSelectedDate(null);
    setSelectedTime('');
    setCalculatedPrice(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setAddress('');
    setNotes('');
    setBookingConfirmed(false);
  };

  return (
    <>
      <section id="pricing" className="py-32" style={{ background: '#FDFDFC' }}>
        <div className="container mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span
              className="text-xs font-bold tracking-[0.2em] uppercase mb-4 inline-block"
              style={{ color: '#0284C7' }}
            >
              PRICING
            </span>
            <h2
              className="text-3xl sm:text-4xl tracking-tight font-medium mb-6"
              style={{ fontFamily: 'Outfit', color: '#0F172A' }}
            >
              Get Your Custom Quote
            </h2>
            <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Calculate your cleaning service cost based on your specific needs
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto bg-white rounded-2xl p-12"
            style={{ border: '1px solid #E2E8F0' }}
          >
            <div className="mb-8">
              <label className="block text-sm font-medium mb-3" style={{ color: '#0F172A' }}>Service Type</label>
              <select
                data-testid="service-type-select"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                className="w-full px-4 py-3 rounded-xl text-base"
                style={{ border: '1px solid #E2E8F0', color: '#0F172A' }}
              >
                <option value="">Select Service</option>
                {serviceTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {isQuoteOnly ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-8 rounded-2xl text-center"
                style={{ background: '#DBEAFE', border: '1px solid #0284C7' }}
              >
                <h3 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                  {serviceType} — Call for a Custom Quote
                </h3>
                <p className="text-base mb-6" style={{ color: '#475569' }}>
                  {serviceType} services are priced case-by-case based on scope, square footage, and specific needs. 
                  Please contact us for a personalized estimate.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <a
                    href="tel:404-750-4446"
                    data-testid="quote-call-btn"
                    className="px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
                    style={{ background: '#0284C7', color: '#FFFFFF' }}
                  >
                    📞 Call 404-750-4446
                  </a>
                  <a
                    href="mailto:Admin@radiuscleaningcompany.com?subject=Quote Request"
                    data-testid="quote-email-btn"
                    className="px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
                    style={{ background: '#FFFFFF', color: '#0284C7', border: '2px solid #0284C7' }}
                  >
                    ✉️ Email for Quote
                  </a>
                </div>
              </motion.div>
            ) : (
              <>
                <div className="mb-8">
                  <label className="block text-sm font-medium mb-3" style={{ color: '#0F172A' }}>Cleaning Level</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      data-testid="cleaning-level-standard-btn"
                      type="button"
                      onClick={() => setCleaningLevel('standard')}
                      className="px-6 py-4 rounded-xl text-left transition-all"
                      style={{
                        border: cleaningLevel === 'standard' ? '2px solid #0284C7' : '1px solid #E2E8F0',
                        background: cleaningLevel === 'standard' ? '#DBEAFE' : '#FFFFFF'
                      }}
                    >
                      <div className="font-semibold text-base" style={{ color: '#0F172A' }}>Standard</div>
                      <div className="text-xs mt-1" style={{ color: '#475569' }}>Regular cleaning at everyday rates</div>
                    </button>
                    <button
                      data-testid="cleaning-level-deep-btn"
                      type="button"
                      onClick={() => setCleaningLevel('deep')}
                      className="px-6 py-4 rounded-xl text-left transition-all"
                      style={{
                        border: cleaningLevel === 'deep' ? '2px solid #0284C7' : '1px solid #E2E8F0',
                        background: cleaningLevel === 'deep' ? '#DBEAFE' : '#FFFFFF'
                      }}
                    >
                      <div className="font-semibold text-base" style={{ color: '#0F172A' }}>Deep Clean</div>
                      <div className="text-xs mt-1" style={{ color: '#475569' }}>Intensive scrubbing — higher rate per room</div>
                    </button>
                  </div>
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-medium mb-4" style={{ color: '#0F172A' }}>
                    Room Breakdown <span className="text-xs font-normal" style={{ color: '#475569' }}>— Enter the number of each room type</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {roomTypes.map((roomName) => (
                      <div key={roomName}>
                        <label className="block text-xs font-medium mb-1" style={{ color: '#475569' }}>{roomName}</label>
                        <input
                          data-testid={`room-${roomName.replace(/\s+/g, '-').toLowerCase()}-input`}
                          type="number"
                          min="0"
                          value={rooms[roomName] || 0}
                          onChange={(e) => setRoomCount(roomName, parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 rounded-lg text-base"
                          style={{ border: '1px solid #E2E8F0', color: '#0F172A' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {!isQuoteOnly && (
              <>
                <div className="mb-8">
                  <label className="block text-sm font-medium mb-3" style={{ color: '#0F172A' }}>Extra Services</label>
                  <div className="grid grid-cols-2 gap-3">
                    {extraServicesList.map((service) => (
                      <button
                        key={service}
                        data-testid={`extra-service-${service.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => handleExtraServiceToggle(service)}
                        className="px-4 py-3 rounded-xl text-base font-medium transition-all"
                        style={{
                          border: '1px solid #E2E8F0',
                          background: extraServices.includes(service) ? '#0284C7' : '#FFFFFF',
                          color: extraServices.includes(service) ? '#FFFFFF' : '#0F172A'
                        }}
                      >
                        {service}
                      </button>
                    ))}
                  </div>
                </div>

                {calculatedPrice && !calculatedPrice.quote_only && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-8 rounded-2xl mb-8"
                    style={{ background: '#F3F4F6' }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <span className="text-2xl font-medium block" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                          Total Price
                        </span>
                        <span className="text-xs" style={{ color: '#475569' }}>
                          {cleaningLevel === 'deep' ? 'Deep Clean pricing applied' : 'Standard pricing applied'}
                          {calculatedPrice.discount_percent > 0 && ` • ${Math.round(calculatedPrice.discount_percent * 100)}% recurring discount`}
                        </span>
                      </div>
                      <span className="text-4xl font-light" style={{ fontFamily: 'Outfit', color: '#0284C7' }}>
                        ${calculatedPrice.total_price.toFixed(2)}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm" style={{ color: '#475569' }}>
                      {calculatedPrice.base_price > 0 && (
                        <div className="flex justify-between">
                          <span>Base Price ({serviceType})</span>
                          <span>${calculatedPrice.base_price.toFixed(2)}</span>
                        </div>
                      )}
                      {calculatedPrice.breakdown && Object.entries(calculatedPrice.breakdown).map(([label, amount]) => (
                        <div key={label} className="flex justify-between">
                          <span>{label}</span>
                          <span style={{ color: amount < 0 ? '#10B981' : '#475569' }}>
                            {amount < 0 ? '-' : ''}${Math.abs(amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                <button
                  data-testid="proceed-to-booking-btn"
                  onClick={() => {
                    if (!serviceType) {
                      toast.error('Please select a service type');
                      return;
                    }
                    setShowBooking(true);
                  }}
                  disabled={!serviceType}
                  className="w-full px-8 py-4 rounded-full font-medium text-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ background: '#0284C7', color: '#FFFFFF' }}
                >
                  Book This Service
                </button>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {showBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.5)' }}
            onClick={() => {
              setShowBooking(false);
              resetForm();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              data-testid="booking-modal"
            >
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-3xl font-medium" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                  {bookingConfirmed ? 'Booking Received' : 'Complete Your Booking'}
                </h3>
                <button
                  data-testid="close-modal-btn"
                  onClick={() => {
                    setShowBooking(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {bookingConfirmed ? (
                <div className="py-8 text-center" data-testid="booking-confirmation-view">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: '#DBEAFE' }}>
                    <CheckCircle2 className="w-12 h-12" style={{ color: '#0284C7' }} />
                  </div>
                  <h4 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
                    Thank you, {customerName.split(' ')[0]}!
                  </h4>
                  <p className="text-base leading-relaxed mb-6 max-w-lg mx-auto" style={{ color: '#475569' }}>
                    We&apos;ve received your booking request for <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')} at {selectedTime}</strong>.
                    A confirmation email is on its way to <strong>{customerEmail}</strong>, and our team will send you an invoice shortly.
                  </p>
                  <div className="p-6 rounded-xl mb-6 max-w-md mx-auto" style={{ background: '#F3F4F6' }}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: '#475569' }}>Estimated Total</span>
                      <span className="text-2xl font-light" style={{ fontFamily: 'Outfit', color: '#0284C7' }}>
                        ${calculatedPrice ? calculatedPrice.total_price.toFixed(2) : '0.00'}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm mb-6" style={{ color: '#475569' }}>
                    Questions? Call us at <a href="tel:404-750-4446" className="font-medium" style={{ color: '#0284C7' }}>404-750-4446</a>
                  </p>
                  <button
                    data-testid="close-confirmation-btn"
                    onClick={() => {
                      setShowBooking(false);
                      resetForm();
                    }}
                    className="px-8 py-3 rounded-full font-medium text-base hover:opacity-90 transition-opacity"
                    style={{ background: '#0284C7', color: '#FFFFFF' }}
                  >
                    Done
                  </button>
                </div>
              ) : (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Full Name *</label>
                  <input
                    data-testid="customer-name-input"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Email *</label>
                  <input
                    data-testid="customer-email-input"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Phone *</label>
                  <input
                    data-testid="customer-phone-input"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    placeholder="(404) 750-4446"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Address *</label>
                  <input
                    data-testid="customer-address-input"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl"
                    style={{ border: '1px solid #E2E8F0' }}
                    placeholder="123 Main St, Atlanta, GA"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Select Date *</label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date()}
                  className="rounded-xl border"
                  style={{ borderColor: '#E2E8F0' }}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Select Time *</label>
                <div className="grid grid-cols-3 gap-3">
                  {timeSlots.map((time) => (
                    <button
                      key={time}
                      data-testid={`time-slot-${time.replace(/\s+/g, '-').toLowerCase()}`}
                      onClick={() => setSelectedTime(time)}
                      className="px-4 py-3 rounded-xl text-base font-medium transition-all"
                      style={{
                        border: '1px solid #E2E8F0',
                        background: selectedTime === time ? '#0284C7' : '#FFFFFF',
                        color: selectedTime === time ? '#FFFFFF' : '#0F172A'
                      }}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: '#0F172A' }}>Additional Notes</label>
                <textarea
                  data-testid="booking-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl"
                  style={{ border: '1px solid #E2E8F0' }}
                  placeholder="Any special instructions..."
                />
              </div>

              {calculatedPrice && (
                <div className="p-6 rounded-xl mb-6" style={{ background: '#F3F4F6' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium" style={{ color: '#0F172A' }}>Total Amount</span>
                    <span className="text-3xl font-light" style={{ fontFamily: 'Outfit', color: '#0284C7' }}>
                      ${calculatedPrice.total_price.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <button
                data-testid="request-invoice-btn"
                onClick={handleSubmitBooking}
                disabled={loading || !serviceType || !selectedDate || !selectedTime || !calculatedPrice}
                className="w-full px-8 py-4 rounded-full font-medium text-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: '#0284C7', color: '#FFFFFF' }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    Request Invoice
                  </>
                )}
              </button>
              <p className="text-xs text-center mt-3" style={{ color: '#64748B' }}>
                No payment required now. We&apos;ll email you an invoice you can pay online.
              </p>
              </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PriceCalculator;
